import React, { useMemo, useState } from "react";
import { Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import { defaultPaginate, PERMISSIONS } from "../../../helpers/constants";
import { SearchRecords } from "../../../components/Common/SearchRecords";
import AutoCompleteSelect from "../../../components/Common/AutoCompleteSelect";
import {
    useGetGalleryFolderListQuery,
} from "../../../store/masters/mastersApis";
import { useGetActivityDropdownQuery } from "../../../store/activity/activityApis";
import { GalleryFolderDrawer } from "../../../components/admin/photo-gallery-manager/GalleryFolderDrawer";
import GalleryFolderTable from "../../../components/admin/photo-gallery-manager/GalleryFolderTable";
import HasPermission from "../../../components/Common/HasPermission";

const defaultFolderValues = {
    name: "",
    activity_id: "",
    description: "",
};

const GalleryFolderMasterContainer = () => {
    const [pagination, setPagination] = useState({
        ...defaultPaginate,
        sortField: "name",
        activity_id: "",
    });
    const [folderDrawer, setFolderDrawer] = useState({
        show: false,
        formType: "Add",
        initialValues: defaultFolderValues,
    });

    const { isLoading, data: galleryFolders, isFetching } = useGetGalleryFolderListQuery(pagination);
    const { data: activityResponse } = useGetActivityDropdownQuery({});

    const activityOptions = useMemo(
        () =>
            (activityResponse || []).map((activity) => ({
                value: activity?.value || activity?._id || "",
                label: activity?.label || activity?.name || "",
            })),
        [activityResponse],
    );
    const selectedActivity =
        activityOptions.find((activity) => activity.value === pagination.activity_id) || null;

    const summaryCards = useMemo(() => {
        const rows = galleryFolders?.result || [];
        return [
            {
                title: "Folders",
                value: galleryFolders?.count || 0,
                helper: "Shared across image and video galleries",
                icon: <FolderRoundedIcon color="success" />,
            },
            {
                title: "Images",
                value: rows.reduce((total, row) => total + (row?.image_count || 0), 0),
                helper: "Visible in current result set",
                icon: <CollectionsRoundedIcon color="primary" />,
            },
            {
                title: "Videos",
                value: rows.reduce((total, row) => total + (row?.video_count || 0), 0),
                helper: "Visible in current result set",
                icon: <OndemandVideoRoundedIcon color="warning" />,
            },
        ];
    }, [galleryFolders]);

    const openFolderDrawer = (formType = "Add", initialValues = defaultFolderValues) => {
        setFolderDrawer({
            show: true,
            formType,
            initialValues,
        });
    };

    const closeFolderDrawer = () => {
        setFolderDrawer({
            show: false,
            formType: "Add",
            initialValues: defaultFolderValues,
        });
    };

    const handleActivityFilterChange = (_, value) => {
        setPagination((prev) => ({
            ...prev,
            pageNo: 0,
            activity_id: value?.value || "",
        }));
    };

    return (
        <Stack spacing={2}>
            <Paper
                sx={{
                    p: 3,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, rgba(16,124,65,0.08) 0%, rgba(7,27,42,0.02) 100%)",
                }}
            >
                <Stack spacing={3}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Stack spacing={1}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Gallery Folder Management
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Create reusable folders, link each one to an existing activity,
                                    and use the same folder library across both photo and video galleries.
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack direction="row" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                                <HasPermission
                                    permissions={[
                                        PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.CREATE,
                                        PERMISSIONS.COMMON_MASTER.VIDEO_GALLERY.CREATE,
                                    ]}
                                    fallback={null}
                                >
                                    <Button
                                        disableElevation
                                        variant="contained"
                                        startIcon={<FolderRoundedIcon />}
                                        sx={{ borderRadius: "14px", minWidth: 160 }}
                                        onClick={() => openFolderDrawer("Add")}
                                    >
                                        New Folder
                                    </Button>
                                </HasPermission>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "rgba(255,255,255,0.72)",
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <SearchRecords handlePagination={setPagination} pagination={pagination} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <AutoCompleteSelect
                                    id="gallery-folder-activity-filter"
                                    options={activityOptions}
                                    label="Filter by Activity"
                                    onChange={handleActivityFilterChange}
                                    value={selectedActivity}
                                    name="activity_id"
                                    keyname="label"
                                    disableCloseOnSelect={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign={{ xs: "left", md: "right" }}
                                >
                                    {selectedActivity
                                        ? `Showing folders linked to ${selectedActivity.label}`
                                        : "Showing folders for all activities"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Grid container spacing={2}>
                        {summaryCards.map((card) => (
                            <Grid item xs={12} md={4} key={card.title}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        height: "100%",
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="overline" color="text.secondary">
                                                {card.title}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                                {card.value}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {card.helper}
                                            </Typography>
                                        </Box>
                                        {card.icon}
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Paper>

            <Paper sx={{ p: 2.5, color: "#071B2A", fontWeight: "400", borderRadius: 3 }} elevation={0}>
                <GalleryFolderTable
                    loading={isLoading}
                    fetching={isFetching}
                    count={galleryFolders?.count || 0}
                    data={galleryFolders?.result || []}
                    pagination={pagination}
                    handlePagination={setPagination}
                    onEdit={(folder) => openFolderDrawer("Edit", folder)}
                />
            </Paper>

            <GalleryFolderDrawer
                show={folderDrawer.show}
                close={closeFolderDrawer}
                formType={folderDrawer.formType}
                initialValues={folderDrawer.initialValues}
            />
        </Stack>
    );
};

export default GalleryFolderMasterContainer;
